import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost } from '../utils/api';

// Define Student interface locally since it's not exported from api
interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string | null;
  age: number | null;
  reading_level: number | null;
  grade_level: string | null;
  assignment_count: number;
  cultures: string[];
  email: string | null;
  student_id: string | null;
  is_active: boolean;
  created_at: string;
  last_activity_date: string | null;
  class_ids: number[];
  class_count: number;
  notes?: string;
}

interface ClassInfo {
  id: number;
  name: string;
  subject?: string;
  grade_level?: string;
  period?: string;
  student_count?: number;
}

interface DuplicateMatch {
  student: Student;
  confidence: number;
  match_reason: string;
  match_details: string;
}

interface DuplicateResolutionModalProps {
  open: boolean;
  onClose: () => void;
  potentialMatches: DuplicateMatch[];
  newStudentData: any;
  onResolve: (action: 'use_existing' | 'create_new', studentId?: number, enrolledClasses?: number[]) => void;
  loading?: boolean;
}

const DuplicateResolutionModal: React.FC<DuplicateResolutionModalProps> = ({
  open,
  onClose,
  potentialMatches,
  newStudentData,
  onResolve,
  loading = false
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [selectedMatch, setSelectedMatch] = useState<DuplicateMatch | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [teacherClasses, setTeacherClasses] = useState<ClassInfo[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const steps = [
    t('duplicateResolution.steps.selectStudent'),
    t('duplicateResolution.steps.assignClasses')
  ];

  // Fetch teacher's classes when moving to step 2
  useEffect(() => {
    if (activeStep === 1 && selectedMatch) {
      fetchTeacherClasses();
    }
  }, [activeStep, selectedMatch]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedMatch(null);
      setSelectedClasses([]);
      setTeacherClasses([]);
    }
  }, [open]);

  const fetchTeacherClasses = async () => {
    if (!token) return;
    
    setLoadingClasses(true);
    try {
      const response = await apiGet('/api/classes?include_students=true', token);
      if (response.ok) {
        const data = await response.json();
        // Filter out classes where the student is already enrolled
        const studentClassIds = selectedMatch?.student.class_ids || [];
        const availableClasses = (data.classes || []).filter(
          (cls: ClassInfo) => !studentClassIds.includes(cls.id)
        );
        setTeacherClasses(availableClasses);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleClassToggle = (classId: number) => {
    setSelectedClasses(prev => 
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClasses.length === teacherClasses.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(teacherClasses.map(cls => cls.id));
    }
  };

  // Don't render if newStudentData is null
  if (!newStudentData) {
    return null;
  }

  const handleUseExisting = async () => {
    if (!selectedMatch) return;
    
    // Move to step 2 (class selection)
    setActiveStep(1);
  };

  const handleConfirmEnrollment = async () => {
    if (!selectedMatch || !token) return;
    
    setEnrolling(true);
    try {
      // Enroll the student in selected classes
      if (selectedClasses.length > 0) {
        for (const classId of selectedClasses) {
          await apiPost(`/api/classes/${classId}/students/${selectedMatch.student.id}`, {}, token);
        }
      }
      
      // Call the resolve callback with the enrolled classes info
      onResolve('use_existing', selectedMatch.student.id, selectedClasses);
    } catch (error) {
      console.error('Error enrolling student:', error);
    } finally {
      setEnrolling(false);
    }
  };

  const handleCreateNew = () => {
    onResolve('create_new');
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedClasses([]);
  };

  const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
    if (confidence >= 90) return 'success';
    if (confidence >= 70) return 'warning';
    return 'error';
  };

  const getMatchReasonText = (reason: string) => {
    switch (reason) {
      case 'exact_google_classroom_id':
        return t('duplicateResolution.matchReasons.googleClassroom');
      case 'exact_email':
        return t('duplicateResolution.matchReasons.email');
      case 'exact_student_id':
        return t('duplicateResolution.matchReasons.studentId');
      case 'fuzzy_name_match':
        return t('duplicateResolution.matchReasons.similarName');
      default:
        return t('duplicateResolution.matchReasons.potential');
    }
  };

  const renderStudentSelection = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('duplicateResolution.newStudent')}:</strong> {newStudentData?.first_name || t('common.unknown')} {newStudentData?.last_name || t('common.student')}
            {newStudentData?.email && ` (${newStudentData.email})`}
            {newStudentData?.student_id && ` - ${t('students.form.studentId')}: ${newStudentData.student_id}`}
          </Typography>
        </Alert>
      </Box>

      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{t('duplicateResolution.matchFoundTitle')}</strong>{' '}
          {t('duplicateResolution.matchFoundDescription')}
        </Typography>
      </Alert>

      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('duplicateResolution.existingStudents')} ({potentialMatches.length})
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {potentialMatches.map((match) => (
          <Card 
            key={match.student.id}
            sx={{ 
              border: selectedMatch?.student.id === match.student.id ? 2 : 1,
              borderColor: selectedMatch?.student.id === match.student.id ? 'primary.main' : 'divider',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 2
              }
            }}
            onClick={() => setSelectedMatch(match)}
            role="button"
            tabIndex={0}
            aria-label={t('duplicateResolution.selectStudent', { name: `${match.student.first_name} ${match.student.last_name}` })}
            aria-pressed={selectedMatch?.student.id === match.student.id}
            onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedMatch(match); }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div">
                  {match.student.first_name} {match.student.last_name}
                </Typography>
                <Chip 
                  label={`${match.confidence}% ${t('duplicateResolution.match')}`}
                  color={getConfidenceColor(match.confidence)}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip 
                  label={getMatchReasonText(match.match_reason)}
                  variant="outlined"
                  size="small"
                />
                {match.student.email && (
                  <Chip label={`${t('duplicateResolution.email')}: ${match.student.email}`} variant="outlined" size="small" />
                )}
                {match.student.student_id && (
                  <Chip label={`${t('students.form.studentId')}: ${match.student.student_id}`} variant="outlined" size="small" />
                )}
                {match.student.grade_level && (
                  <Chip label={`${t('duplicateResolution.grade')}: ${match.student.grade_level}`} variant="outlined" size="small" />
                )}
                {match.student.class_count > 0 && (
                  <Chip 
                    label={t('duplicateResolution.enrolledInClasses', { count: match.student.class_count })}
                    variant="outlined" 
                    size="small"
                    color="info"
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                {match.match_details}
              </Typography>

              {match.student.notes && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {t('duplicateResolution.notes')}: {match.student.notes}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ 
        p: 2, 
        bgcolor: 'grey.50', 
        borderRadius: 1,
        border: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('duplicateResolution.createNew')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('duplicateResolution.createNewDescription', { name: `${newStudentData?.first_name || t('common.unknown')} ${newStudentData?.last_name || t('common.student')}` })}
        </Typography>
        <Typography variant="body2" color="warning.main">
          <strong>{t('duplicateResolution.note')}:</strong> {t('duplicateResolution.createNewWarning')}
        </Typography>
      </Box>
    </>
  );

  const renderClassSelection = () => (
    <>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('duplicateResolution.addingStudent')}:</strong> {selectedMatch?.student.first_name} {selectedMatch?.student.last_name}
        </Typography>
      </Alert>

      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('duplicateResolution.selectClasses')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('duplicateResolution.selectClassesDescription')}
      </Typography>

      {loadingClasses ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : teacherClasses.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedMatch?.student.class_ids && selectedMatch.student.class_ids.length > 0
            ? t('duplicateResolution.alreadyEnrolledInAllClasses')
            : t('duplicateResolution.noClasses')}
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={selectedClasses.length === teacherClasses.length}
                  indeterminate={selectedClasses.length > 0 && selectedClasses.length < teacherClasses.length}
                  onChange={handleSelectAll}
                />
              }
              label={t('duplicateResolution.selectAll')}
            />
          </Box>

          <FormGroup sx={{ pl: 2 }}>
            {teacherClasses.map((cls) => (
              <FormControlLabel
                key={cls.id}
                control={
                  <Checkbox
                    checked={selectedClasses.includes(cls.id)}
                    onChange={() => handleClassToggle(cls.id)}
                    aria-label={t('duplicateResolution.enrollIn', { className: cls.name })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{cls.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[cls.subject, cls.grade_level, cls.period].filter(Boolean).join(' • ')}
                      {cls.student_count !== undefined && ` • ${cls.student_count} ${t('duplicateResolution.studentsEnrolled')}`}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            ))}
          </FormGroup>
        </>
      )}
    </>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="duplicate-modal-title"
      aria-describedby="duplicate-modal-description"
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle id="duplicate-modal-title">
        <Typography variant="h6" component="div">
          {activeStep === 0 
            ? t('duplicateResolution.title')
            : t('duplicateResolution.assignToClasses')}
        </Typography>
        <Typography id="duplicate-modal-description" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {activeStep === 0 
            ? t('duplicateResolution.description', { count: potentialMatches.length, name: `${newStudentData?.first_name || t('common.unknown')} ${newStudentData?.last_name || t('common.student')}` })
            : t('duplicateResolution.classSelectionDescription')}
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent>
        {activeStep === 0 ? renderStudentSelection() : renderClassSelection()}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {activeStep === 0 ? (
          <>
            <Button 
              onClick={onClose} 
              disabled={loading}
              aria-label={t('common.cancel')}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateNew}
              variant="outlined"
              disabled={loading}
              aria-label={t('duplicateResolution.createNewStudent')}
            >
              {t('duplicateResolution.createNewStudent')}
            </Button>
            <Button 
              onClick={handleUseExisting}
              variant="contained"
              disabled={!selectedMatch || loading}
              aria-label={t('duplicateResolution.useSelectedStudent')}
            >
              {t('duplicateResolution.useSelectedStudent')}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleBack} 
              disabled={enrolling}
              aria-label={t('common.back')}
            >
              {t('common.back')}
            </Button>
            <Button 
              onClick={handleConfirmEnrollment}
              variant="contained"
              disabled={enrolling}
              aria-label={selectedClasses.length > 0 
                ? t('duplicateResolution.addToClasses', { count: selectedClasses.length })
                : t('duplicateResolution.skipAndFinish')}
            >
              {enrolling ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              {selectedClasses.length > 0 
                ? t('duplicateResolution.addToClasses', { count: selectedClasses.length })
                : t('duplicateResolution.skipAndFinish')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateResolutionModal;
